import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { scApi, FC110 } from '../../../services/scApi';

interface UseSCApprovalOptions {
  record: FC110 | null;
  isEditing: boolean;
  reviewScNo: string | null;
  loadRecordWithNames: (scNo: string) => Promise<void>;
}

export function useSCApproval({ record, isEditing, reviewScNo, loadRecordWithNames }: UseSCApprovalOptions) {
  const navigate = useNavigate();
  const [approvalHistoryVisible, setApprovalHistoryVisible] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將 S/C ${record.fc110_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await scApi.submitForApproval(record.fc110_01);
          message.success(resp.message || '送審成功');
          await loadRecordWithNames(record.fc110_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准 S/C ${record.fc110_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await scApi.approve(record.fc110_01);
          message.success(resp.message || '核准成功');
          if (reviewScNo) {
            navigate('/erp/approvals');
          } else {
            await loadRecordWithNames(record.fc110_01);
          }
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames, reviewScNo, navigate]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回 S/C ${record.fc110_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await scApi.reject(record.fc110_01);
          message.success(resp.message || '退回成功');
          if (reviewScNo) {
            navigate('/erp/approvals');
          } else {
            await loadRecordWithNames(record.fc110_01);
          }
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames, reviewScNo, navigate]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fc110_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await scApi.writeoff(record.fc110_01);
          message.success(resp.message || '沖銷成功');
          await loadRecordWithNames(record.fc110_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleViewHistory = useCallback(() => {
    if (!record) return;
    setApprovalHistoryVisible(true);
  }, [record]);

  return {
    approvalHistoryVisible,
    setApprovalHistoryVisible,
    handleSubmit,
    handleApprove,
    handleReject,
    handleWriteoff,
    handleViewHistory,
  };
}
